import { Grid } from "@mui/material";
import { FormikProps, getIn } from "formik";
import React from "react";
import Flex from "../../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../../components/Text";
import { Form2Payload } from "../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import { RISK_LEVEL_OPTIONS } from "./InitialPsychotherapyConstant";
import { ClearIcon } from "@mui/x-date-pickers";

// Section shape shared by PSYCHIATRIC_HISTORY_SECTIONS and
// PHYSICAL_ASSESSMENT_EXAM_SECTIONS. The selected options are stored as a
// comma-separated string in `formik.values[key]` and the free-text note in
// `noteKey` (defaults to `${key}Note`).
type SummarySection = {
  key: string;
  label: string;
  items: readonly { name: string; label: string }[];
  noteKey?: string;
};

interface SelectedSectionsSummaryProps {
  formik: FormikProps<Form2Payload>;
  sections: readonly SummarySection[];
  // Optional trailing free-text note not tied to a section, e.g. the MSE
  // "Overall MSE Note" stored at `overallMseNote`.
  overallNoteKey?: string;
  overallNoteLabel?: string;
  // Hide the bold section heading. Useful when the summary sits directly under
  // an inline field group that already shows the label (modality forms).
  showSectionLabel?: boolean;
}

const parseSelected = (value: unknown): string[] =>
  typeof value === "string" ? value.split(",").filter(Boolean) : [];

// Renders the selected values (3 per horizontal row, bullet-pointed) with an
// optional note for each section. Sections with no selection and no note are
// hidden. Read-only summary shown in the visit note template (ECRH-5418).
export const SelectedSectionsSummary: React.FC<
  SelectedSectionsSummaryProps
> = ({
  formik,
  sections,
  overallNoteKey,
  overallNoteLabel = "Note",
  showSectionLabel = true,
}) => {
  const visibleSections = sections
    .map((section) => {
      const selectedNames = parseSelected(getIn(formik.values, section.key));
      const selectedItems = section.items.filter((item) =>
        selectedNames.includes(item.name)
      );
      const noteKey = section.noteKey ?? `${section.key}Note`;
      const note = (getIn(formik.values, noteKey) as string) || "";
      return { section, selectedItems, note };
    })
    .filter(({ selectedItems, note }) => selectedItems.length > 0 || !!note);

  const overallNote = overallNoteKey
    ? (getIn(formik.values, overallNoteKey) as string) || ""
    : "";

  if (visibleSections.length === 0 && !overallNote) return null;

  // Remove a single selected value by rebuilding the section's
  // comma-separated string without it.
  const removeValue = (sectionKey: string, name: string) => {
    const remaining = parseSelected(getIn(formik.values, sectionKey)).filter(
      (n) => n !== name
    );
    formik.setFieldValue(sectionKey, remaining.join(","));
  };

  return (
    <Flex flexDirection="column" gap={1} width="100%" mt={0.5}>
      {visibleSections.map(({ section, selectedItems, note }) => (
        <Flex flexDirection="column" gap={0.25} width="100%" key={section.key}>
          {showSectionLabel && (
            <Text variant="subtitle1" fontWeight="bold">
              {section.label}
            </Text>
          )}

          {selectedItems.length > 0 && (
            <Grid container spacing={0.2}>
              {selectedItems.map((item) => (
                <Grid key={item.name} size={12}>
                  <Flex alignItems={"center"} gap={0.5}>
                    <Text fontSize="14px">{`• ${item.label}`}</Text>

                    <ClearIcon
                      onClick={() => removeValue(section.key, item.name)}
                      fontSize="small"
                      sx={{ cursor: "pointer", fontSize: "14px" }}
                      color="error"
                    />
                  </Flex>
                </Grid>
              ))}
            </Grid>
          )}

          {!!note && (
            <Text fontSize="14px" mt={0.5}>
              <Text component="span" fontSize="14px" fontWeight="bold">
                Note:{" "}
              </Text>
              {note}
            </Text>
          )}
        </Flex>
      ))}

      {!!overallNote && (
        <Text fontSize="14px">
          <Text component="span" fontSize="14px" fontWeight="bold">
            {`${overallNoteLabel}: `}
          </Text>
          {overallNote}
        </Text>
      )}
    </Flex>
  );
};

interface RiskAssessmentSummaryProps {
  formik: FormikProps<Form2Payload>;
}

// Read-only summary for the Risk Assessment section: single risk level plus an
// optional note (ECRH-5418).
export const RiskAssessmentSummary: React.FC<RiskAssessmentSummaryProps> = ({
  formik,
}) => {
  const riskLevel = (formik.values?.riskLevel as string) || "";
  const note = (formik.values?.riskLevelNote as string) || "";
  const levelLabel = RISK_LEVEL_OPTIONS.find(
    (option) => option.value === riskLevel
  )?.label;

  if (!levelLabel && !note) return null;

  return (
    <Flex flexDirection="column" gap={0.25} width="100%" mt={0.5}>
      {!!levelLabel && (
        <Text fontSize="14px">
          <Text component="span" fontSize="14px" fontWeight="bold">
            Level:{" "}
          </Text>
          {levelLabel}
        </Text>
      )}
      {!!note && (
        <Text fontSize="14px">
          <Text component="span" fontSize="14px" fontWeight="bold">
            Note:{" "}
          </Text>
          {note}
        </Text>
      )}
    </Flex>
  );
};
